import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Clock, Loader2, Plus, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtINR } from "@/lib/documents";

/** Auto-suggests a late-checkout fee when the guest is still in-house past the hotel's check-out time. */
export function LateCheckoutBanner({ stayId, active = true, onApplied }) {
  const [ev, setEv] = useState(null);
  const [busy, setBusy] = useState(null);
  const [waiving, setWaiving] = useState(false);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");

  const load = useCallback(async () => {
    if (!stayId || !active) return;
    try { const { data } = await api.get(`/pms/stays/${stayId}/late-checkout`); setEv(data); setAmount(String(data.suggested_fee || "")); }
    catch { setEv(null); }
  }, [stayId, active]);
  useEffect(() => { load(); }, [load]);

  if (!ev || !active) return null;
  if (ev.already_charged) return (
    <div className="flex items-center gap-2 text-[11.5px] text-emerald-500 border border-emerald-500/20 bg-emerald-500/5 rounded-md px-3 py-2" data-testid="late-checkout-charged">
      <Clock className="w-3.5 h-3.5" /> Late checkout fee posted to folio today ({ev.minutes_late} min past {ev.configured_checkout_time}).
    </div>);
  if (ev.waived) return (
    <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground border border-hocp-border rounded-md px-3 py-2" data-testid="late-checkout-waived">
      <Clock className="w-3.5 h-3.5" /> Late checkout fee waived — {ev.waive_reason}
    </div>);
  if (!ev.should_suggest) return null;

  const apply = () => run("apply", () => api.post(`/pms/stays/${stayId}/late-checkout/apply`, { amount: +amount || undefined }), `Late checkout fee ${fmtINR(+amount || ev.suggested_fee)} added to folio`);
  const waive = () => run("waive", () => api.post(`/pms/stays/${stayId}/late-checkout/waive`, { reason: reason.trim() }), "Late checkout fee waived");
  const run = async (k, fn, ok) => {
    setBusy(k);
    try { await fn(); toast.success(ok); await load(); onApplied?.(); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="border border-amber-500/40 bg-amber-500/10 rounded-md p-3 space-y-2" data-testid="late-checkout-banner">
      <div className="flex items-start gap-2">
        <Clock className="w-4 h-4 text-amber-400 mt-0.5" />
        <div className="text-[12.5px] flex-1">
          <span className="font-semibold text-amber-300">Late checkout detected</span> — guest is <span className="mono" data-testid="late-checkout-minutes">{Math.floor(ev.minutes_late / 60)}h {ev.minutes_late % 60}m</span> past the {ev.configured_checkout_time} check-out time.
          <div className="text-muted-foreground text-[11.5px]">Policy: {ev.pct}% of nightly rate {fmtINR(ev.nightly_rate)} → suggested fee <span className="font-semibold text-foreground" data-testid="late-checkout-suggested">{fmtINR(ev.suggested_fee)}</span>{ev.tax_rate ? ` + GST ${ev.tax_rate}% = ${fmtINR(ev.total)}` : ""}.</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pl-6">
        <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-hocp-bgCard border-hocp-border h-8 w-32 mono text-[12px]" data-testid="late-checkout-amount" />
        <Button size="sm" disabled={busy === "apply" || !(+amount > 0)} onClick={apply} className="h-8 text-[12px] bg-amber-500 hover:bg-amber-600 text-black" data-testid="late-checkout-apply-btn">
          {busy === "apply" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}Add to folio</Button>
        {!waiving ? <Button size="sm" variant="ghost" onClick={() => setWaiving(true)} className="h-8 text-[12px]" data-testid="late-checkout-waive-btn"><Ban className="w-3.5 h-3.5 mr-1" />Waive</Button>
          : <>
            <Input placeholder="Waive reason…" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-hocp-bgCard border-hocp-border h-8 w-56 text-[12px]" data-testid="late-checkout-waive-reason" />
            <Button size="sm" variant="ghost" disabled={reason.trim().length < 3 || busy === "waive"} onClick={waive} className="h-8 text-[12px]" data-testid="late-checkout-waive-confirm">Confirm waive</Button>
          </>}
      </div>
    </div>
  );
}
